import { getOrderDetail, getOrderUpdate, restoreOrderPoints } from '@/api/order';
import { FORMAT_TIME } from '@/types/common';
import { useQuery } from '@tanstack/react-query';
import { Button, Descriptions, Drawer, Form, Input, message, Modal, Tag } from 'antd';
import dayjs from 'dayjs';
import { OrderStatus } from './OrderTable';
import ProductImg from './ProductImg';
import { useEffect, useState } from 'react';
import { OrderTypeMap } from '@/types/order';

interface OrderDetailProps {
  orderId?: string;
  visible: boolean;
  onClose: () => void;
  onRefreshList: () => void;
  listItem?: any; // 列表中的订单数据，用于获取 productShowImages
}

const OrderDetail = ({ orderId, visible, onClose, onRefreshList, listItem }: OrderDetailProps) => {
  const [messageApi, contextHolder] = message.useMessage();
  const [form] = Form.useForm();

  const [loading, setLoading] = useState(false);

  const { data: orderDetail, refetch } = useQuery({
    queryKey: ['orderDetail', orderId],
    queryFn: async () => {
      if (!orderId) return null;
      const resp = await getOrderDetail(orderId);
      if (resp.status !== 200) {
        return null;
      }
      return resp.data;
    },
    enabled: !!orderId,
  });

  const handleRestore = () => {
    Modal.confirm({
      title: '确认退还积分',
      content: '确定要退还该订单的积分吗？',
      onOk: async () => {
        if (!orderId) return;
        const resp = await restoreOrderPoints(orderId);
        if (resp.status !== 200) {
          messageApi.error('退还积分失败：' + resp.message);
          return;
        }
        messageApi.success('退还积分成功');
        void refetch();
      },
    });
  };

  const renderOrderStatus = (status?: number) => {
    switch (status) {
      case OrderStatus.Created:
        return <Tag color="processing">已创建</Tag>;
      case OrderStatus.ToShip:
        return <Tag color="warning">待发货</Tag>;
      case OrderStatus.ToReceive:
        return <Tag color="warning">待收货</Tag>;
      case OrderStatus.Completed:
        return <Tag color="success">已完成</Tag>;
      case OrderStatus.Canceled:
        return <Tag color="default">已取消</Tag>;
      case OrderStatus.Refunded:
        return <Tag color="error">已退款</Tag>;
      case OrderStatus.Deleted:
        return <Tag color="default">已删除</Tag>;
      default:
        return <Tag color="default">未知</Tag>;
    }
  };

  const handleSubmit = async () => {
    const value = await form.validateFields();
    console.log(value)

    setLoading(true);
    const res = await getOrderUpdate({
      ...value,
      orderId
    });

    if (res.status === 200) {
      refetch();
      onRefreshList()
    } else {
      message.error("更新失败");
    }

    setLoading(false);

  }

  useEffect(() => {
    if (!orderDetail) return;
    if ([OrderStatus.ToShip, OrderStatus.ToReceive].includes(orderDetail.orderStatus)) {
      form.setFieldsValue(orderDetail);
    }
  }, [orderDetail]);

  return (
    <Drawer
      title="订单详情"
      width={600}
      open={visible}
      onClose={onClose}
      maskClosable={false}
      extra={
        orderDetail?.orderStatus === OrderStatus.Created && (
          <Button type="primary" danger onClick={handleRestore}>
            退还积分
          </Button>
        )
      }
    >
      {contextHolder}
      {orderDetail && (
        <div>
          <h3>订单信息</h3>
          <Descriptions column={1}>
            <Descriptions.Item label="商品名称">
              {orderDetail.productTitle}
            </Descriptions.Item>
            <Descriptions.Item label="商品预览">
              <ProductImg value={orderDetail.productShowImages ?? listItem?.productShowImages} />
            </Descriptions.Item>
            <Descriptions.Item label="SUPP ID">
              {orderDetail.userNumber || ""}
            </Descriptions.Item>
            <Descriptions.Item label="用户名称">
              {orderDetail.userName}
            </Descriptions.Item>
            <Descriptions.Item label="订单类型">
              {orderDetail.orderType ? OrderTypeMap.get(orderDetail.orderType) : "-"}
            </Descriptions.Item>
            <Descriptions.Item label="订单状态">
              {renderOrderStatus(orderDetail.orderStatus)}
            </Descriptions.Item>
            <Descriptions.Item label="商品ID">
              {orderDetail.productInfoId}
            </Descriptions.Item>
            <Descriptions.Item label="商品库存ID">
              {orderDetail.productStockId}
            </Descriptions.Item>
            <Descriptions.Item label="商品数量">
              {orderDetail.productCount}
            </Descriptions.Item>
            <Descriptions.Item label="商品价格">
              {orderDetail.points}
            </Descriptions.Item>
            <Descriptions.Item label="订单总金额">
              {orderDetail.sumPoints}
            </Descriptions.Item>

            {orderDetail.price && (
              <Descriptions.Item label="商品价格">
                {orderDetail.price}
              </Descriptions.Item>
            )}
            {orderDetail.sumPrice && (
              <Descriptions.Item label="订单总价">
                {orderDetail.sumPrice}
              </Descriptions.Item>
            )}
            {orderDetail.minusPrice && (
              <Descriptions.Item label="优惠金额">
                {orderDetail.minusPrice}
              </Descriptions.Item>
            )}
            {orderDetail.payPrice && (
              <Descriptions.Item label="支付金额">
                {orderDetail.payPrice}
              </Descriptions.Item>
            )}
          </Descriptions>
          <h3>物流信息</h3>
          <Descriptions column={1}>
            <Descriptions.Item label="收货人姓名">
              {orderDetail.fullName || '-'}
            </Descriptions.Item>
            <Descriptions.Item label="电话">
              {orderDetail.phoneNumber || '-'}
            </Descriptions.Item>
            <Descriptions.Item label="邮箱">
              {orderDetail.email || '-'}
            </Descriptions.Item>
            <Descriptions.Item label="收货地址">
              <div>{orderDetail.address || '-'}</div>

            </Descriptions.Item>
            {
              [OrderStatus.ToShip, OrderStatus.ToReceive].includes(orderDetail.orderStatus) ? (
                <Descriptions.Item label="物流信息">
                  <Form form={form} labelCol={{ span: 6 }} style={{ width: '100%' }}>
                    <Form.Item label="物流公司" name="logisticsCompany" rules={[{ required: true }]}>
                      <Input maxLength={30} />
                    </Form.Item>
                    <Form.Item label="运单号" name="trackingNum" rules={[{ required: true }]}>
                      <Input maxLength={30} />
                    </Form.Item>
                    <Form.Item>
                      <Button loading={loading} type='primary' onClick={handleSubmit} style={{ marginLeft: '100%', transform: 'translateX(-100%)' }}>
                        {orderDetail.orderStatus === OrderStatus.ToShip ? '提交' : '更新'}
                      </Button>
                    </Form.Item>
                  </Form>
                </Descriptions.Item>
              ) : (
                <>
                  <Descriptions.Item label="物流公司">
                    {orderDetail.logisticsCompany || '-'}
                  </Descriptions.Item>
                  <Descriptions.Item label="运单号">
                    {orderDetail.trackingNum || '-'}
                  </Descriptions.Item>
                </>
              )
            }

          </Descriptions>
          <h3>交易信息</h3>
          <Descriptions column={1}>
            <Descriptions.Item label="订单编号">
              {orderDetail.orderId}
            </Descriptions.Item>
            <Descriptions.Item label="下单时间">
              {dayjs(orderDetail.createTime).format(FORMAT_TIME)}
            </Descriptions.Item>
            <Descriptions.Item label="支付时间">
              {orderDetail.payTime
                ? dayjs(orderDetail.payTime).format(FORMAT_TIME)
                : '-'}
            </Descriptions.Item>
          </Descriptions>
        </div>
      )}
    </Drawer>
  );
};

export default OrderDetail;
